import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';
import QAndA from '../organisms/QAndA';
import GradingFields from '../atoms/GradingFields';
import IndividualQuestionStudentHistory from './IndividualQuestionStudentHistory';
import { fetchUserAnswersAsOfDate, fetchStudentGrades, determineTargetDate, fetchStudentAnswersForExam } from '../utils/serverFetchAndAddUtilities';
import { CourseIDContext } from '../context/CourseIDContext';
import './GradingInterface.css';

const GradingInterface = ({ evaluationId, username, onReturn }) => {

  const { courseID } = useContext(CourseIDContext);
  const { courseDesignState, questionByQuestionGrading } = useContext(CourseDesignContext);
  const [answerHistory, setAnswerHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targetDate, setTargetDate] = useState(null);
  const [historyModalQuestionID, setHistoryModalQuestionID] = useState(null);
  const modalCloseRef = useRef(null);

  const openHistoryModal = useCallback((questionID) => {
    setHistoryModalQuestionID(questionID);
  }, []);

  const closeHistoryModal = useCallback(() => {
    setHistoryModalQuestionID(null);
  }, []);

  useEffect(() => {
    if (!historyModalQuestionID) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') closeHistoryModal(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [historyModalQuestionID, closeHistoryModal]);

  // Move focus to the close button when the modal opens
  useEffect(() => {
    if (historyModalQuestionID && modalCloseRef.current) {
      modalCloseRef.current.focus();
    }
  }, [historyModalQuestionID]);
  
  // reload answers when custom due date is added
  const handleCustomDueDateChange = (newDueDate) => {
    setTargetDate(newDueDate); // Update targetDate with the new custom due date
  };

// determine the target date for grading based on exam due date or custom due date (if there is one)
  useEffect(() => {
  const fetchTargetDate = async () => {
    if (!courseID || !username || !evaluationId) return;

    const targetDate = await determineTargetDate(courseID, username, evaluationId, courseDesignState, fetchStudentGrades);
    setTargetDate(targetDate);
  };

  fetchTargetDate();
}, [courseID, username, evaluationId, courseDesignState]);

// Log targetDate whenever it updates
useEffect(() => {
  console.log('Updated target date:', targetDate);
}, [targetDate]);

  // Fetch answer history when the component mounts or when courseID/username changes
useEffect(() => {
  const fetchAnswers = async () => {
    if (!courseID || !username || !targetDate) return;

    setLoading(true);
    try {
      const answers = await fetchStudentAnswersForExam(courseID, username, targetDate, fetchUserAnswersAsOfDate);
      setAnswerHistory(answers); // Set the processed answers directly to state
    } catch (error) {
      setAnswerHistory(null);
    } finally {
      setLoading(false);
    }
  };

  fetchAnswers();
}, [courseID, username, targetDate]);

// NEW: Add this after your existing useEffects (e.g. after the fetchAnswers effect)
useEffect(() => {
  const refreshAnswers = async () => {
    if (!courseID || !username || !targetDate) return;
    try {
      const answers = await fetchStudentAnswersForExam(
        courseID,
        username,
        targetDate,
        fetchUserAnswersAsOfDate
      );
      setAnswerHistory(answers);
    } catch (error) {
      console.error("Error refreshing student answers:", error);
    }
  };

  window.addEventListener("focus", refreshAnswers);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      refreshAnswers();
    }
  });

  return () => {
    window.removeEventListener("focus", refreshAnswers);
    document.removeEventListener("visibilitychange", refreshAnswers);
  };
}, [courseID, username, targetDate]);



const sectionsToShow = useMemo(() => {
  const evaluation = courseDesignState.find(item => item.dataID === evaluationId && item.dataType === 'evaluation');
  return evaluation?.questionOptions || [];
}, [courseDesignState, evaluationId]);

  console.log('evaluation id from grading interface:', evaluationId);
  console.log('sections to show calculated in grading interface:', sectionsToShow);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="grading-interface" style={{ display: 'flex', width: '100%', height: '100vh', backgroundColor: '#ebf3f7' }}>
      <div className="left-panel" style={{ width: '40%', padding: '20px', overflowY: 'auto', height: '100%'}}>
        <GradingFields
          username={username}
          evaluationId={evaluationId}
          onCustomDueDateChange={handleCustomDueDateChange}
        />
      </div>
      <div className="right-panel" style={{ width: '60%', padding: '20px', overflowY: 'auto', height: '100%'}}>
        <QAndA
          username={username}
          ignoreCurrentCourseDate={true}
          gradeEdit={questionByQuestionGrading}
          answersReadOnly={true}
          sectionsToShow={sectionsToShow}
          showSectionHeaders={true}
          evaluationID={evaluationId}
          studentAnswers={answerHistory}
          showQuestionHistoryButton={true}
          onOpenHistoryModal={openHistoryModal}
        />
      </div>

      {historyModalQuestionID && (
        <div className="historyModalOverlay" onClick={closeHistoryModal}>
          <div className="historyModalPanel" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <button
              ref={modalCloseRef}
              className="historyModalClose"
              onClick={closeHistoryModal}
              aria-label="Close history"
            >
              ×
            </button>
            <IndividualQuestionStudentHistory
              username={username}
              questionID={historyModalQuestionID}
              showSnapshotPicker={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GradingInterface;
